import type { SupabaseClient } from '@supabase/supabase-js';
import { isPilotOnly, type SupportScope } from '@teslyn/domain';
import { loadMySupport } from '../support/api.ts';
import {
  isClientRole,
  normaliseTenantRole,
  sortTenantRoles,
  type ClientRole,
  type TenantRole,
} from './roles.ts';

/** One place the signed-in user may work in on this tenant's portal. */
export type AccessContext =
  | {
      readonly kind: 'workspace';
      readonly key: string;
      readonly tenantId: string;
      readonly tenantSlug: string;
      readonly tenantName: string;
      /** Every tenant role the user holds here (one membership row each), in TENANT_ROLES order; never empty. */
      readonly roles: readonly TenantRole[];
      /**
       * Teslyn Support access (decision 800): a platform admin who is not a member works in the company's data while
       * its tenant admin's grant is in force (or the staging switch is on). The roles then only choose the pages
       * shown; the database decides what the grant allows.
       */
      readonly support?: {
        readonly scope: SupportScope;
        readonly endsAt: string | null;
        readonly standing: boolean;
      };
    }
  | {
      readonly kind: 'client';
      readonly key: string;
      readonly tenantId: string;
      readonly tenantSlug: string;
      readonly clientOrgId: string;
      readonly clientOrgName: string;
      readonly role: ClientRole;
    }
  | { readonly kind: 'platform'; readonly key: 'platform' };

export interface Access {
  readonly platformAdmin: boolean;
  /** Contexts on the current tenant's portal, in display order. */
  readonly contexts: readonly AccessContext[];
  /** Memberships the user has on other tenants' portals (shown on the not-linked page). */
  readonly elsewhere: readonly string[];
  /**
   * Organisations on this portal where the user is a pilot only (R156, decision 917): Teslyn Mobile only, so no
   * workspace; the pilot page says so when nothing else is open to them.
   */
  readonly pilotOnly: readonly string[];
}

interface TenantEmbed {
  readonly id: string;
  readonly slug: string;
  readonly name: string;
}
interface MembershipRow {
  readonly tenant_id: string;
  readonly role: string;
  readonly tenant: TenantEmbed | null;
}
interface ClientMembershipRow {
  readonly client_org_id: string;
  readonly role: string;
  readonly client_org: {
    readonly id: string;
    readonly name: string;
    readonly tenant_id: string;
    readonly tenant: TenantEmbed | null;
  } | null;
}

function one<T>(v: T | T[] | null | undefined): T | null {
  if (Array.isArray(v)) return v[0] ?? null;
  return v ?? null;
}

/**
 * Load the user's memberships. RLS returns only the rows the user may see; we filter to the user's own rows and
 * to the tenant whose portal this is.
 */
export async function loadAccess(sb: SupabaseClient, userId: string, tenantSlug: string): Promise<Access> {
  const [me, mem, cmem] = await Promise.all([
    sb.from('app_user').select('platform_admin').eq('id', userId).maybeSingle(),
    sb.from('membership').select('tenant_id, role, tenant:tenant_id (id, slug, name)').eq('user_id', userId),
    sb
      .from('client_membership')
      .select(
        'client_org_id, role, client_org:client_org_id (id, name, tenant_id, tenant:tenant_id (id, slug, name))',
      )
      .eq('user_id', userId),
  ]);
  for (const r of [me, mem, cmem]) if (r.error) throw new Error(r.error.message);

  const platformAdmin = Boolean((me.data as { platform_admin?: boolean } | null)?.platform_admin);
  const contexts: AccessContext[] = [];
  const elsewhere = new Set<string>();

  // One membership row per role: gather a tenant's rows into one workspace with all of its roles.
  const workspaces = new Map<string, { tenant: TenantEmbed; roles: TenantRole[] }>();
  for (const raw of (mem.data ?? []) as unknown as MembershipRow[]) {
    const t = one(raw.tenant);
    const role = normaliseTenantRole(raw.role);
    if (!t || !role) continue;
    if (t.slug !== tenantSlug) {
      elsewhere.add(t.name);
      continue;
    }
    const w = workspaces.get(t.id) ?? { tenant: t, roles: [] };
    w.roles.push(role);
    workspaces.set(t.id, w);
  }
  const pilotOnly: string[] = [];
  for (const { tenant: t, roles } of workspaces.values()) {
    // a pilot-only sign-in has no Teslyn Cloud pages (decision 917): no workspace, the pilot page instead
    if (isPilotOnly(roles)) {
      pilotOnly.push(t.name);
      continue;
    }
    contexts.push({
      kind: 'workspace',
      key: `w:${t.id}`,
      tenantId: t.id,
      tenantSlug: t.slug,
      tenantName: t.name,
      roles: sortTenantRoles(roles),
    });
  }
  for (const raw of (cmem.data ?? []) as unknown as ClientMembershipRow[]) {
    const org = one(raw.client_org);
    const t = one(org?.tenant);
    if (!org || !t || !isClientRole(raw.role)) continue;
    if (t.slug !== tenantSlug) {
      elsewhere.add(t.name);
      continue;
    }
    contexts.push({
      kind: 'client',
      key: `c:${org.id}`,
      tenantId: t.id,
      tenantSlug: t.slug,
      clientOrgId: org.id,
      clientOrgName: org.name,
      role: raw.role,
    });
  }
  // Teslyn Support (decision 800): a platform admin with no workspace here, while this company's grant is in force
  if (platformAdmin && workspaces.size === 0) {
    const support = await supportContext(sb, tenantSlug);
    if (support) contexts.push(support);
  }
  if (platformAdmin) contexts.push({ kind: 'platform', key: 'platform' });
  return { platformAdmin, contexts, elsewhere: [...elsewhere].sort(), pilotOnly: pilotOnly.sort() };
}

/** The pages a support context shows (a manager's, office staff's and a data processor's; never a tenant admin's). */
export const SUPPORT_ROLES: readonly TenantRole[] = sortTenantRoles([
  'manager',
  'office_staff',
  'data_processor',
]);

/** The company's support context for a platform admin, when a grant is in force there (or the staging switch). */
async function supportContext(sb: SupabaseClient, tenantSlug: string): Promise<AccessContext | null> {
  const t = await sb.from('tenant').select('id, slug, name').eq('slug', tenantSlug).maybeSingle();
  const row = t.data as TenantEmbed | null;
  if (t.error || !row) return null;
  const mine = await loadMySupport(sb, row.id).catch(() => null);
  if (!mine) return null;
  return {
    kind: 'workspace',
    key: `s:${row.id}`,
    tenantId: row.id,
    tenantSlug: row.slug,
    tenantName: row.name,
    roles: SUPPORT_ROLES,
    support: { scope: mine.scope, endsAt: mine.endsAt, standing: mine.standing },
  };
}
